import React from "react";
import {
    Theme,
    StandardProps,
    Typography,
    LinearProgress,
    IconButton
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import { StyleClassKey } from "js/react/typescript";
import {
    UploadProgress,
    UploadStatus
} from "js/react/scenes/components/MediaManagement";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: theme.spacing(0.75, 1),
            paddingBottom: theme.spacing(0.5),
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            borderBottom: `1px solid ${theme.palette.grey["300"]}`
        },

        delIcon: {
            padding: theme.spacing(0.5),
            marginRight: theme.spacing(1),
            "& svg": {
                fontSize: "1.1rem",
                color: theme.palette.error.main
            }
        },
        error: {
            color: theme.palette.error.main,
            fontStyle: "italic"
        },
        name: {
            color: theme.palette.common.white,
            fontSize: "0.85rem"
        },
        infoRoot: {
            display: "flex",
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "space-between"
        },
        progress: {
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: "0.95rem"
        },
        progressBar: {
            flexGrow: 1,
            "& .MuiLinearProgress-barColorPrimary": {
                backgroundColor: theme.palette.primary.main
            }
        },
        failed: {
            "& .MuiLinearProgress-barColorPrimary": {
                backgroundColor: theme.palette.error.main
            }
        },
        completed: {
            "& .MuiLinearProgress-barColorPrimary": {
                backgroundColor: theme.palette.success.main
            }
        },
        failText: {
            color: theme.palette.error.main,
            marginLeft: theme.spacing(1),
            fontStyle: "italic"
        }
    });

type PendingUploadRowClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);

export interface PendingUploadRowProps
    extends StandardProps<{}, PendingUploadRowClassKey> {
    upload: UploadProgress;
    onDelete: () => void;
}

function Component(props: PendingUploadRowProps) {
    const classes = useStyles(props);
    const { upload, onDelete } = props;

    return (
        <div className={classes.root}>
            <div className={classes.infoRoot}>
                <Typography variant="body2" className={classes.name}>
                    {upload.fileName}
                    {upload.status === UploadStatus.FAILED && (
                        <span className={classes.failText}>Failed</span>
                    )}
                </Typography>
                <Typography variant="body2" className={classes.progress}>
                    {upload.progress + "%"}
                </Typography>
            </div>
            <div className={classes.infoRoot}>
                {upload.status !== UploadStatus.IN_PROGRESS && (
                    <IconButton
                        className={classes.delIcon}
                        onClick={() => onDelete()}
                    >
                        <FontAwesomeIcon icon={["fal", "times"]} />
                    </IconButton>
                )}
                <LinearProgress
                    className={clsx(classes.progressBar, {
                        [classes.failed]: upload.status === UploadStatus.FAILED,
                        [classes.completed]:
                            upload.status === UploadStatus.COMPLETED
                    })}
                    variant="determinate"
                    color={"primary"}
                    value={upload.progress}
                />
            </div>
        </div>
    );
}

Component.defaultProps = {} as PendingUploadRowProps;
Component.displayName = "PendingUploadRow";
export const PendingUploadRow = Component;
export default PendingUploadRow;
